/**
 * Candidates page: funnel tabs, filters, quick actions (правка №4/№14, Чат 1). [Claude] 2026-11-03
 * Pure helpers — the backend applies the same tab rules (app/candidates/list_filters.py).
 */
import { LEGACY_STATUS_MAP } from "./candidateStatuses";

export const FUNNEL_TABS = [
    { key: "all", label: "Все" },
    { key: "new", label: "Неразобранные" },
    { key: "test", label: "Тест" },
    { key: "interview", label: "Собеседование" },
    { key: "offer", label: "Оффер" },
    { key: "hire", label: "Найм и адаптация" },
    { key: "rejected", label: "Отказы" },
    { key: "archive", label: "Архив" },
];

export const TAB_KEYS = FUNNEL_TABS.map((tab) => tab.key);

export const TAB_STATUSES = {
    new: ["неразобранный", "холодный поиск"],
    test: ["тест: отправлен", "тест: звонок", "тест: пройден (частично)", "тест: пройден", "НДЗ: тест"],
    interview: [
        "собес: назначен", "собес: не пришёл", "собес: перенос", "собес: ожидает решения",
        "тест день: назначен", "тест день: ожидает решения", "тест день: не пришёл",
    ],
    offer: ["оффер: отправлен", "оффер: принят"],
    hire: [
        "обучение", "ВНР: запрос документов", "ВНР: документы направлены",
        "адаптация: 1 неделя", "адаптация: 1 месяц", "адаптация: 2 месяца", "в штате",
    ],
    rejected: [
        "не подходит: резюме", "не подходит: тест", "не подходит: звонок", "не подходит: собес",
        "не подходит: тест день", "не подходит: оффер",
        "отказался: тест", "отказался: собес", "отказался: тест день", "отказался: оффер",
        "отказался: обучение", "отказался: ВНР",
    ],
};

export function canonicalStatus(status) {
    const raw = String(status ?? "").trim();
    return LEGACY_STATUS_MAP[raw] || raw;
}

/** Funnel tab of a status ("new", "test", …) or null. */
export function tabOfStatus(status) {
    const value = canonicalStatus(status);
    return Object.keys(TAB_STATUSES).find((key) => TAB_STATUSES[key].includes(value)) || null;
}

/** Request scope of a tab. «Отказы» also look into the archive. */
export function tabScope(tab) {
    if (tab === "archive") return { category: "archive", stage: null, includeArchived: false };
    if (tab === "rejected") return { category: "candidate", stage: "rejected", includeArchived: true };
    if (TAB_KEYS.includes(tab) && tab !== "all") return { category: "candidate", stage: tab, includeArchived: false };
    return { category: "candidate", stage: null, includeArchived: false };
}

/** Refusal status that fits where the candidate is now. */
export function rejectStatusFor(status) {
    const tab = tabOfStatus(status);
    if (tab === "new" || !tab) return "не подходит: резюме";
    if (tab === "test") return "не подходит: тест";
    return "не подходит: собес";
}

export const SORT_OPTIONS = [
    { value: "activity:desc", label: "По последней активности" },
    { value: "applied:desc", label: "Сначала новые отклики" },
    { value: "applied:asc", label: "Сначала старые отклики" },
    { value: "ai_score:desc", label: "По ИИ-оценке" },
    { value: "salary:asc", label: "Зарплата: по возрастанию" },
    { value: "salary:desc", label: "Зарплата: по убыванию" },
    { value: "age:asc", label: "Возраст: моложе" },
    { value: "age:desc", label: "Возраст: старше" },
    { value: "name:asc", label: "ФИО: А → Я" },
    { value: "name:desc", label: "ФИО: Я → А" },
];

export const EXPERIENCE_OPTIONS = [
    { value: "noExperience", label: "Нет опыта" },
    { value: "between1And3", label: "1–3 года" },
    { value: "between3And6", label: "3–6 лет" },
    { value: "moreThan6", label: "Более 6 лет" },
];

export const GENDER_OPTIONS = [
    { value: "", label: "Любой" },
    { value: "male", label: "Мужской" },
    { value: "female", label: "Женский" },
];

export const EMPTY_FILTERS = {
    search: "",
    vacancy_ids: [],
    statuses: [],
    sort_by: "activity",
    sort_dir: "desc",
    age_from: "",
    age_to: "",
    salary_from: "",
    salary_to: "",
    city: "",
    gender: "",
    experience: [],
    ai_score_min: "",
    applied_from: "",
    applied_to: "",
    perfect: false,
    not_sync: false,
};

const LIST_KEYS = ["vacancy_ids", "statuses", "experience"];

export function normalizeFilters(raw) {
    const source = raw && typeof raw === "object" ? raw : {};
    const out = { ...EMPTY_FILTERS };
    for (const key of Object.keys(EMPTY_FILTERS)) {
        if (!(key in source) || source[key] === null || source[key] === undefined) continue;
        const value = source[key];
        if (LIST_KEYS.includes(key)) out[key] = Array.isArray(value) ? value.map(String).filter(Boolean) : [];
        else if (typeof EMPTY_FILTERS[key] === "boolean") out[key] = value === true;
        else out[key] = String(value);
    }
    return out;
}

const num = (value) => {
    if (value === "" || value === null || value === undefined) return undefined;
    const n = Number(String(value).replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(n) ? n : undefined;
};

/** Filters → extra options of getCandidates / getCandidateStageCounts. */
export function filtersToRequest(filters) {
    const f = normalizeFilters(filters);
    return {
        statuses: f.statuses.length ? f.statuses : undefined,
        ageFrom: num(f.age_from),
        ageTo: num(f.age_to),
        salaryFrom: num(f.salary_from),
        salaryTo: num(f.salary_to),
        city: f.city.trim() || undefined,
        gender: f.gender || undefined,
        experience: f.experience.length ? f.experience : undefined,
        aiScoreMin: num(f.ai_score_min),
        appliedFrom: f.applied_from || undefined,
        appliedTo: f.applied_to || undefined,
        sortBy: f.sort_by || undefined,
        sortDir: f.sort_dir || undefined,
    };
}

/** Number of filters hidden under «Ещё фильтры». */
export function moreFiltersCount(filters) {
    const f = normalizeFilters(filters);
    let count = 0;
    if (f.age_from || f.age_to) count += 1;
    if (f.salary_from || f.salary_to) count += 1;
    if (f.city.trim()) count += 1;
    if (f.gender) count += 1;
    if (f.experience.length) count += 1;
    if (f.ai_score_min) count += 1;
    if (f.applied_from || f.applied_to) count += 1;
    if (f.perfect) count += 1;
    if (f.not_sync) count += 1;
    return count;
}

const range = (from, to, unit = "") => {
    if (from && to) return `${from}–${to}${unit}`;
    if (from) return `от ${from}${unit}`;
    return `до ${to}${unit}`;
};

const ruDate = (value) => {
    const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}.${m[2]}.${m[1].slice(2)}` : String(value || "");
};

/**
 * Chips of active filters: [{ id, label, patch }] — patch resets the filter.
 * vacancyName(id) gives the vacancy title.
 */
export function activeFilterChips(filters, { vacancyName = (id) => `Вакансия ${id}` } = {}) {
    const f = normalizeFilters(filters);
    const chips = [];
    for (const id of f.vacancy_ids) {
        chips.push({ id: `vacancy:${id}`, label: vacancyName(id), patch: { vacancy_ids: f.vacancy_ids.filter((x) => x !== id) } });
    }
    for (const status of f.statuses) {
        chips.push({ id: `status:${status}`, label: status, patch: { statuses: f.statuses.filter((x) => x !== status) } });
    }
    if (f.age_from || f.age_to) chips.push({ id: "age", label: `Возраст ${range(f.age_from, f.age_to)}`, patch: { age_from: "", age_to: "" } });
    if (f.salary_from || f.salary_to) chips.push({ id: "salary", label: `Зарплата ${range(f.salary_from, f.salary_to, " ₽")}`, patch: { salary_from: "", salary_to: "" } });
    if (f.city.trim()) chips.push({ id: "city", label: `Город: ${f.city.trim()}`, patch: { city: "" } });
    if (f.gender) chips.push({ id: "gender", label: f.gender === "male" ? "Мужчины" : "Женщины", patch: { gender: "" } });
    if (f.experience.length) {
        const labels = f.experience.map((v) => EXPERIENCE_OPTIONS.find((o) => o.value === v)?.label || v);
        chips.push({ id: "experience", label: `Опыт: ${labels.join(", ")}`, patch: { experience: [] } });
    }
    if (f.ai_score_min) chips.push({ id: "ai", label: `ИИ-оценка от ${f.ai_score_min}`, patch: { ai_score_min: "" } });
    if (f.applied_from || f.applied_to) {
        const label = f.applied_from && f.applied_to
            ? `${ruDate(f.applied_from)}–${ruDate(f.applied_to)}`
            : f.applied_from ? `с ${ruDate(f.applied_from)}` : `по ${ruDate(f.applied_to)}`;
        chips.push({ id: "applied", label: `Отклик ${label}`, patch: { applied_from: "", applied_to: "" } });
    }
    if (f.perfect) chips.push({ id: "perfect", label: "Идеальные кандидаты", patch: { perfect: false } });
    if (f.not_sync) chips.push({ id: "not_sync", label: "Не синхронизированы с hh", patch: { not_sync: false } });
    return chips;
}

/** Filters a user may reset (search and sort are kept). */
export function resetFilters(filters) {
    const f = normalizeFilters(filters);
    return { ...EMPTY_FILTERS, search: f.search, sort_by: f.sort_by, sort_dir: f.sort_dir };
}

export const QUICK_ACTIONS = [
    { key: "interview", label: "Пригласить на собеседование", short: "Собес" },
    { key: "test", label: "Отправить тест", short: "Тест" },
    { key: "consider", label: "Ожидает решения", short: "Подумать" },
    { key: "reject", label: "Отказать", short: "Отказать" },
    { key: "offer", label: "Отправить оффер", short: "Оффер" },
    { key: "comment", label: "Комментарий", short: "Коммент." },
    { key: "hh", label: "Резюме на hh.ru", short: "hh" },
    { key: "phone", label: "Позвонить", short: "Звонок" },
    { key: "telegram", label: "Написать в Telegram", short: "TG" },
    { key: "edit", label: "Редактировать", short: "Ред." },
    { key: "delete", label: "Удалить", short: "Удалить" },
];

export const QUICK_ACTION_KEYS = QUICK_ACTIONS.map((a) => a.key);
export const DEFAULT_QUICK_ACTIONS = ["interview", "test", "reject", "comment"];
export const PAGE_SIZES = [20, 50, 100];

/** Blocks of a card in the hh-like list; order and visibility — personal (доработка 03.11). */
export const LIST_BLOCKS = ["ai", "experience", "jobs", "location", "skills", "education", "languages", "contacts", "comment"];
export const DEFAULT_LIST_BLOCKS = ["ai", "experience", "jobs", "location", "skills", "education", "contacts", "comment"];

export const DEFAULT_VIEW_PREFS = {
    view: "list",
    density: "detailed",
    list_blocks: DEFAULT_LIST_BLOCKS,
    quick_actions: DEFAULT_QUICK_ACTIONS,
    page_size: 20,
    tab: "all",
    filters: {},
};

export function normalizeViewPrefs(raw) {
    const p = raw && typeof raw === "object" ? raw : {};
    return {
        view: p.view === "table" ? "table" : "list",
        density: p.density === "compact" ? "compact" : "detailed",
        list_blocks: Array.isArray(p.list_blocks)
            ? p.list_blocks.filter((k, i, all) => LIST_BLOCKS.includes(k) && all.indexOf(k) === i)
            : DEFAULT_LIST_BLOCKS,
        quick_actions: Array.isArray(p.quick_actions)
            ? p.quick_actions.filter((k, i, all) => QUICK_ACTION_KEYS.includes(k) && all.indexOf(k) === i)
            : DEFAULT_QUICK_ACTIONS,
        page_size: PAGE_SIZES.includes(Number(p.page_size)) ? Number(p.page_size) : 20,
        tab: TAB_KEYS.includes(p.tab) ? p.tab : "all",
        filters: p.filters && typeof p.filters === "object" ? p.filters : {},
    };
}

/** Quick actions available for a candidate in its current state. */
export function availableQuickActions(keys, candidate, { archived = false } = {}) {
    return (keys || []).filter((key) => {
        if (key === "hh") return Boolean(candidate?.hh_resume_link);
        if (key === "phone") return Boolean(candidate?.phone_number);
        if (key === "telegram") return Boolean(candidate?.telegram_username);
        if (archived && ["interview", "test", "consider", "reject", "offer"].includes(key)) return false;
        return true;
    });
}

/** Table sort column ↔ server sort key. */
/** Table sort column ↔ server sort key; every column sorts over the whole list (доработка 03.11). */
export const COLUMN_SORT_KEYS = {
    full_name: "name",
    status: "status",
    vacancy: "vacancy",
    applied_at: "applied",
    ai_score: "ai_score",
    ai_comment: "ai_comment",
    age: "age",
    city: "city",
    salary: "salary",
    work_total: "experience",
    experience: "experience",
    last_job: "last_job",
    avg_tenure: "avg_tenure",
    skills: "skills",
    education: "education",
    languages: "languages",
    gender: "gender",
    phone: "phone",
    email: "email",
    telegram: "telegram",
    comment: "comment",
    next_contact: "next_contact",
    resume_update: "resume_update",
    active_search: "active_search",
};

export function tableSortFromFilters(filters) {
    const f = normalizeFilters(filters);
    const column = Object.keys(COLUMN_SORT_KEYS).find((id) => COLUMN_SORT_KEYS[id] === f.sort_by);
    return column ? { key: column, dir: f.sort_dir === "asc" ? "asc" : "desc" } : null;
}

export function filtersFromTableSort(sort) {
    if (!sort || !COLUMN_SORT_KEYS[sort.key]) return { sort_by: "activity", sort_dir: "desc" };
    return { sort_by: COLUMN_SORT_KEYS[sort.key], sort_dir: sort.dir === "asc" ? "asc" : "desc" };
}
